// KPI tab — two-view orchestrator: Definitions (table of KPI definitions
// with CRUD/archive) and Overview (new three-layer panel: summary strip,
// compliance chart, sparkline card grid). Default view is Overview when
// any KPI has ≥1 report, else Definitions.
//
// F3 archived deep-link banner + drawer (the legacy detail modal is
// retired). F5 post-Create highlights the new KPI in
// Definitions view. N1 filter invariant preserved. Deep link
// `?tab=kpi&item=<id>` selects the KPI — the Overview panel opens the
// drawer for that id.
// Contract-scoped report + measurement queries are lazy — only active
// when the Overview view is mounted or a deep-link is present.

import { useMemo, useRef, useState } from "react";
import { Pencil, Archive as ArchiveIcon } from "lucide-react";
import Alert from "../../../common/Alert";
import Button from "../../../common/Button";
import TrackingTabShell from "../shared/TrackingTabShell";
import TrackingTable, { type Column } from "../shared/TrackingTable";
import ArchiveDialog from "../shared/ArchiveDialog";
import KpiFormModal, { type KpiFormMode } from "./KpiFormModal";
import KpiDetailExpand from "./KpiDetailExpand";
import KpiOverviewPanel from "./overview/KpiOverviewPanel";
import BulkPeriodEntryModal from "./bulk/BulkPeriodEntryModal";
import {
  useArchiveTrackingItem,
  useCreateTrackingItem,
  useOrgWideTracking,
  useTrackingItem,
  useTrackingItems,
  useUpdateTrackingItem,
} from "../../../../hooks/useTracking";
import { useManagedContract } from "../../../../hooks/useManagedContracts";
import type {
  CreateKpiInput,
  Kpi,
  KpiMeasurement,
  KpiReport,
  UpdateKpiInput,
} from "../../../../types";

interface KpiTabProps {
  contractId: string;
  itemIdFromUrl?: string | null;
}

type ViewMode = "definitions" | "overview";

const PAGE_SIZE = 25;
const OVERVIEW_DATA_LIMIT = 500;

export default function KpiTab({
  contractId,
  itemIdFromUrl,
}: KpiTabProps) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [nextTokenStack, setNextTokenStack] = useState<string[]>([]);
  const currentToken = nextTokenStack[nextTokenStack.length - 1];
  const [viewMode, setViewMode] = useState<ViewMode | null>(null);

  const [modalMode, setModalMode] = useState<KpiFormMode | null>(null);
  const [modalSource, setModalSource] = useState<Kpi | undefined>();
  const [archiveTarget, setArchiveTarget] = useState<Kpi | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  // Inline-expand row ID — row click opens the read-only DetailExpand
  // below the row. Pencil still opens the full-screen edit modal. Seeded
  // from the ?item= deep link so the definitions table opens the linked
  // KPI's detail without a second click (the Overview view's drawer is
  // driven by selectedKpiId below, seeded the same way).
  const [expandedRowId, setExpandedRowId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  const [archiveBanner, setArchiveBanner] = useState(true);
  // Selected KPI drives the Overview panel's drawer mount.
  const [selectedKpiId, setSelectedKpiId] = useState<string | null>(
    itemIdFromUrl ?? null,
  );
  // Bulk-entry modal visibility — triggered by the Overview header's
  // "Log period (bulk)" secondary button. Mounted below the existing
  // modal stack so it can coexist with the Definitions drawer.
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isPending, error } = useTrackingItems(
    contractId,
    "kpi",
    { limit: PAGE_SIZE, nextToken: currentToken, includeArchived },
  );

  const items: Kpi[] = useMemo(
    () =>
      (data?.items ?? []).filter((i): i is Kpi => i.type === "kpi"),
    [data],
  );

  // "Any report exists" heuristic — use org-wide kpi-report query as a
  // lightweight signal for default-view choice. Not authoritative.
  const reportsQuery = useOrgWideTracking("kpi-report", { limit: 1 });
  const anyReports = (reportsQuery.data?.items ?? []).some(
    (r): r is KpiReport =>
      r.type === "kpi-report" && r.contractId === contractId,
  );
  const effectiveView: ViewMode =
    viewMode ?? (anyReports ? "overview" : "definitions");

  // Contract-scoped reports + measurements — only fetched when the
  // Overview view is active or a deep-link is present. Definitions-only
  // users never pay the query cost.
  const overviewDataEnabled =
    effectiveView === "overview" || !!itemIdFromUrl;
  const contractReportsQuery = useTrackingItems(
    contractId,
    "kpi-report",
    { limit: OVERVIEW_DATA_LIMIT },
    { enabled: overviewDataEnabled },
  );
  const contractMeasurementsQuery = useTrackingItems(
    contractId,
    "kpi-measurement",
    { limit: OVERVIEW_DATA_LIMIT },
    { enabled: overviewDataEnabled },
  );

  const contractReports: KpiReport[] = useMemo(
    () =>
      (contractReportsQuery.data?.items ?? []).filter(
        (i): i is KpiReport => i.type === "kpi-report",
      ),
    [contractReportsQuery.data],
  );
  const contractMeasurements: KpiMeasurement[] = useMemo(
    () =>
      (contractMeasurementsQuery.data?.items ?? []).filter(
        (i): i is KpiMeasurement => i.type === "kpi-measurement",
      ),
    [contractMeasurementsQuery.data],
  );

  // Contract-level compliance target powers the chart reference line +
  // summary-strip compliance-rate tile. Null/undefined falls back to 95
  // inside the Overview panel, so the hook can tolerate a still-loading
  // contract without the chart flickering to a wrong target value.
  const contractQuery = useManagedContract(contractId);
  const complianceTarget = contractQuery.data?.complianceTarget ?? null;

  const deepLinkInPage = itemIdFromUrl
    ? items.some((i) => i.id === itemIdFromUrl)
    : false;
  const missingItemQuery = useTrackingItem(
    contractId,
    "kpi",
    !deepLinkInPage && itemIdFromUrl ? itemIdFromUrl : "",
  );
  const missingItem =
    missingItemQuery.data && missingItemQuery.data.type === "kpi"
      ? (missingItemQuery.data as Kpi)
      : null;

  const createMut = useCreateTrackingItem();
  const updateMut = useUpdateTrackingItem();
  const archiveMut = useArchiveTrackingItem();

  const handleOpenCreate = () => {
    setModalSource(undefined);
    setModalMode("create");
  };
  const handleOpenEdit = (item: Kpi) => {
    setModalSource(item);
    setModalMode("edit");
  };
  const handleCloseModal = () => setModalMode(null);

  const handleCreate = async (input: CreateKpiInput) => {
    const created = (await createMut.mutateAsync({
      contractId,
      type: "kpi",
      input,
    })) as Kpi | undefined;
    if (created?.id) setHighlightRowId(created.id);
  };

  const handleUpdate = async (input: UpdateKpiInput) => {
    if (!modalSource) return;
    await updateMut.mutateAsync({
      contractId,
      type: "kpi",
      itemId: modalSource.id,
      input,
    });
  };

  const handleArchiveConfirm = async (reason: string) => {
    if (!archiveTarget) return;
    await archiveMut.mutateAsync({
      contractId,
      type: "kpi",
      itemId: archiveTarget.id,
      reason,
    });
    setArchiveTarget(null);
  };

  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const toggleExpand = (item: Kpi) => {
    setExpandedRowId((prev) => (prev === item.id ? null : item.id));
  };

  const columns: Column<Kpi>[] = [
    { header: "#", cell: (i) => i.kpiNumber, width: "w-12" },
    { header: "Name", cell: (i) => i.name },
    { header: "Target", cell: (i) => i.target },
    { header: "Measurement", cell: (i) => i.measurementFrequency },
    { header: "Reporting", cell: (i) => i.reportingFrequency },
    { header: "Service Credits", cell: (i) => (i.isSla ? "Yes" : "—") },
    {
      header: "Actions",
      align: "right",
      cell: (i) => (
        <div className="inline-flex gap-1">
          <Button
            variant="ghost"
            iconOnly
            size="sm"
            icon={<Pencil className="h-4 w-4" />}
            aria-label={`Edit ${i.name}`}
            title={`Edit ${i.name}`}
            onClick={(e) => {
              e.stopPropagation();
              rowRefs.current[i.id] = e.currentTarget;
              handleOpenEdit(i);
            }}
          />
          <Button
            variant="ghost"
            iconOnly
            size="sm"
            icon={<ArchiveIcon className="h-4 w-4" />}
            aria-label={`Archive ${i.name}`}
            title={`Archive ${i.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setArchiveTarget(i);
            }}
          />
        </div>
      ),
    },
  ];

  const paginationState = {
    canPrev: nextTokenStack.length > 0,
    canNext: !!data?.nextToken,
    onPrev: () => setNextTokenStack((s) => s.slice(0, -1)),
    onNext: () =>
      data?.nextToken &&
      setNextTokenStack((s) => [...s, data.nextToken as string]),
    page: nextTokenStack.length + 1,
    totalPages: data?.nextToken
      ? nextTokenStack.length + 2
      : nextTokenStack.length + 1,
  };

  const sortedForOverview = useMemo(
    () => [...items].sort((a, b) => a.kpiNumber - b.kpiNumber),
    [items],
  );

  return (
    <div className="space-y-4">
      {itemIdFromUrl && !deepLinkInPage && missingItem && archiveBanner && (
        <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {missingItem.archivedAt
                ? `This KPI was archived.`
                : `This KPI is not shown in the current list.`}
            </span>
            <button
              type="button"
              className="underline"
              onClick={() => {
                setViewMode("overview");
                setSelectedKpiId(missingItem.id);
              }}
            >
              View archived item
            </button>
          </div>
        </Alert>
      )}

      {itemIdFromUrl &&
        !deepLinkInPage &&
        missingItemQuery.error &&
        archiveBanner && (
          <Alert variant="info" onDismiss={() => setArchiveBanner(false)}>
            Item not found. It may have been deleted.
          </Alert>
        )}

      <div className="flex justify-start">
        <div
          role="tablist"
          aria-label="KPI view"
          className="inline-flex gap-1 rounded-md border border-slate-200 p-1 dark:border-slate-700"
        >
          <button
            type="button"
            role="tab"
            aria-selected={effectiveView === "definitions"}
            className={`rounded-sm px-3 py-1 text-caption ${
              effectiveView === "definitions"
                ? "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200"
                : "text-slate-600 dark:text-slate-400"
            }`}
            onClick={() => setViewMode("definitions")}
          >
            Definitions
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveView === "overview"}
            className={`rounded-sm px-3 py-1 text-caption ${
              effectiveView === "overview"
                ? "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-200"
                : "text-slate-600 dark:text-slate-400"
            }`}
            onClick={() => setViewMode("overview")}
          >
            Overview
          </button>
        </div>
      </div>

      <TrackingTabShell
        type="kpi"
        onAdd={handleOpenCreate}
        /* On Overview, defining a new KPI is a rare structural action;
           the 80% path is "Log period (bulk)". Swap variants so the
           bulk-log button is visually primary and the New-KPI button
           demotes to secondary. */
        addVariant={effectiveView === "overview" ? "secondary" : "primary"}
        /* "New KPI" rather than the default "Add KPI" — the verb
           disambiguates from the sibling primary action `Log period
           (bulk)`, which also reads as adding-something to a user. */
        addLabel="New KPI"
        includeArchived={includeArchived}
        onToggleArchived={(v) => {
          setIncludeArchived(v);
          setNextTokenStack([]);
        }}
        isEmpty={!isPending && !error && items.length === 0}
        paginationState={paginationState}
      >
        {effectiveView === "definitions" ? (
          <TrackingTable<Kpi>
            columns={columns}
            items={items}
            isLoading={isPending}
            error={error}
            emptyMessage="No KPIs recorded yet."
            rowKey={(i) => i.id}
            expandedRowId={expandedRowId}
            highlightRowId={highlightRowId}
            onRowClick={toggleExpand}
            renderExpandedRow={(item) => (
              <KpiDetailExpand
                item={item}
                onEdit={
                  item.archivedAt
                    ? undefined
                    : () => {
                        setExpandedRowId(null);
                        handleOpenEdit(item);
                      }
                }
              />
            )}
          />
        ) : (
          <KpiOverviewPanel
            contractId={contractId}
            kpis={sortedForOverview}
            reports={contractReports}
            measurements={contractMeasurements}
            complianceTarget={complianceTarget}
            selectedKpiId={selectedKpiId}
            fallbackSelectedKpi={missingItem}
            onSelectKpi={setSelectedKpiId}
            onAddKpi={handleOpenCreate}
            onEditKpi={handleOpenEdit}
            onBulkLogPeriod={() => setBulkOpen(true)}
          />
        )}
      </TrackingTabShell>

      <KpiFormModal
        isOpen={modalMode === "create"}
        onClose={handleCloseModal}
        mode="create"
        contractId={contractId}
        existingKpis={items}
        onCreate={handleCreate}
        isSubmitting={createMut.isPending}
      />
      <KpiFormModal
        isOpen={modalMode === "edit"}
        onClose={handleCloseModal}
        mode="edit"
        contractId={contractId}
        source={modalSource}
        onUpdate={handleUpdate}
        isSubmitting={updateMut.isPending}
      />

      <ArchiveDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        itemLabel={
          archiveTarget ? `KPI "${archiveTarget.name}"` : "this KPI"
        }
        isSubmitting={archiveMut.isPending}
      />

      <BulkPeriodEntryModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        contractId={contractId}
        kpis={sortedForOverview}
        measurements={contractMeasurements}
        reports={contractReports}
      />
    </div>
  );
}
